import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertCircle,
  Maximize2,
  ArrowUpRight,
  Crop,
  Paintbrush,
  X,
  Check,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Trash2,
  Grid2X2,
  Grid3X3,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useCreateModel } from './context'
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGES } from './constants'
import { readImageDimensions, detectIssues } from './validation'
import type { TrainingImage } from './types'

// ─── Fix action icon mapping ────────────────────────────────────

const FIX_ICONS: Record<string, React.ReactNode> = {
  upscale: <ArrowUpRight className="h-3 w-3" />,
  crop: <Crop className="h-3 w-3" />,
  inpaint: <Paintbrush className="h-3 w-3" />,
  outpaint: <Maximize2 className="h-3 w-3" />,
  remove: <X className="h-3 w-3" />,
}

// ─── Image Card ─────────────────────────────────────────────────

function ImageCard({ image, compact }: { image: TrainingImage; compact: boolean }) {
  const { state, dispatch } = useCreateModel()
  const isSelected = state.expandedImageId === image.id
  const primaryIssue = image.issues[0]

  const handleClick = useCallback(() => {
    dispatch({ type: 'SET_EXPANDED_IMAGE', payload: isSelected ? null : image.id })
  }, [dispatch, image.id, isSelected])

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch({ type: 'REMOVE_IMAGE', payload: image.id })
  }, [dispatch, image.id])

  const handleFixIssue = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (primaryIssue) {
      dispatch({ type: 'DISMISS_ISSUE', payload: { imageId: image.id, issueType: primaryIssue.type } })
    }
  }, [dispatch, image.id, primaryIssue])

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          'group relative aspect-square cursor-pointer overflow-hidden rounded-lg bg-muted transition-all',
          isSelected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
        )}
        onClick={handleClick}
      >
        <img src={image.objectUrl} alt={image.name} className="h-full w-full object-cover" />

        {/* Hover overlay with caption preview */}
        <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 transition-opacity group-hover:opacity-100">
          {/* Caption text */}
          {image.caption && (
            <p className={cn('px-2 pb-2 text-white', compact ? 'text-[10px] leading-tight' : 'text-xs leading-snug line-clamp-3')}>
              {image.caption}
            </p>
          )}
          {!image.caption && (
            <p className={cn('px-2 pb-2 text-white/60 italic', compact ? 'text-[10px]' : 'text-xs')}>
              Click to caption
            </p>
          )}
        </div>

        {/* Remove button */}
        <button
          onClick={handleRemove}
          className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
        >
          <X className="h-2.5 w-2.5" />
        </button>

        {/* Status indicators (bottom corners, always visible) */}
        {!compact && (
          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[9px] text-white/80">
            {image.width}x{image.height}
          </span>
        )}

        {/* Caption status dot */}
        {image.captionStatus !== 'none' && (
          <div className={cn(
            'absolute top-1 left-1 flex h-4 w-4 items-center justify-center rounded-full',
            image.captionStatus === 'auto' ? 'bg-blue-500/90' : 'bg-green-500/90',
          )}>
            {image.captionStatus === 'auto' ? (
              <Sparkles className="h-2 w-2 text-white" />
            ) : (
              <Check className="h-2 w-2 text-white" />
            )}
          </div>
        )}

        {/* Issue indicator */}
        {primaryIssue && (
          <div className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive/90">
            <AlertCircle className="h-2.5 w-2.5 text-white" />
          </div>
        )}
      </div>

      {/* Issue row below card (only in non-compact) */}
      {!compact && primaryIssue && (
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-destructive">{primaryIssue.label}</span>
          <Button variant="outline" size="sm" className="h-5 gap-0.5 px-1 text-[10px]" onClick={handleFixIssue}>
            {FIX_ICONS[primaryIssue.fixAction]}
            {primaryIssue.fixLabel}
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Caption Editor ─────────────────────────────────────────────

function CaptionEditor() {
  const { state, dispatch } = useCreateModel()
  const image = state.expandedImageId ? state.images.find((i) => i.id === state.expandedImageId) : null
  const currentIndex = image ? state.images.findIndex((i) => i.id === image.id) : -1
  const canPrev = currentIndex > 0
  const canNext = currentIndex < state.images.length - 1

  const navigatePrev = useCallback(() => {
    if (canPrev) dispatch({ type: 'SET_EXPANDED_IMAGE', payload: state.images[currentIndex - 1]!.id })
  }, [canPrev, currentIndex, state.images, dispatch])

  const navigateNext = useCallback(() => {
    if (canNext) dispatch({ type: 'SET_EXPANDED_IMAGE', payload: state.images[currentIndex + 1]!.id })
  }, [canNext, currentIndex, state.images, dispatch])

  useEffect(() => {
    if (!image) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'ArrowLeft') navigatePrev()
      if (e.key === 'ArrowRight') navigateNext()
      if (e.key === 'Escape') dispatch({ type: 'SET_EXPANDED_IMAGE', payload: null })
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [image, navigatePrev, navigateNext, dispatch])

  if (!image) return null

  return (
    <div className="flex gap-3 rounded-lg border border-border bg-card p-3">
      {/* Preview */}
      <div className="h-[180px] w-[180px] shrink-0 overflow-hidden rounded-md bg-muted">
        <img src={image.objectUrl} alt={image.name} className="h-full w-full object-cover" />
      </div>

      {/* Right */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium truncate">{image.name}</p>
            <p className="text-[11px] text-muted-foreground">
              {image.width}x{image.height} &middot; {(image.sizeBytes / 1024 / 1024).toFixed(1)}MB
            </p>
          </div>
          <div className="flex items-center gap-1">
            {image.captionStatus !== 'none' && (
              <Badge variant={image.captionStatus === 'auto' ? 'secondary' : 'success'} size="sm">
                {image.captionStatus === 'auto' ? 'Auto' : 'Edited'}
              </Badge>
            )}
            <Button variant="ghost" size="icon-xs" onClick={() => dispatch({ type: 'SET_EXPANDED_IMAGE', payload: null })}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Caption */}
        <Textarea
          size="sm"
          placeholder="Describe this image for training..."
          value={image.caption}
          onChange={(e) => dispatch({ type: 'UPDATE_CAPTION', payload: { imageId: image.id, caption: e.target.value, status: 'manual' } })}
          className="flex-1 resize-none text-xs"
          rows={3}
        />

        {/* Actions + nav */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]"
              onClick={() => {
                const prefix = state.triggerWord ? `${state.triggerWord} ` : ''
                dispatch({ type: 'UPDATE_CAPTION', payload: { imageId: image.id, caption: prefix + 'regenerated description of this image', status: 'auto' } })
              }}>
              <RotateCcw className="h-2.5 w-2.5" />Re-caption
            </Button>
            <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]"
              onClick={() => dispatch({ type: 'UPDATE_CAPTION', payload: { imageId: image.id, caption: '', status: 'manual' } })}>
              <Trash2 className="h-2.5 w-2.5" />Clear
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="icon-xs" onClick={navigatePrev} disabled={!canPrev}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-[11px] text-muted-foreground">{currentIndex + 1}/{state.images.length}</span>
            <Button variant="ghost" size="icon-xs" onClick={navigateNext} disabled={!canNext}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Add Image Card ─────────────────────────────────────────────

function AddCard({ compact }: { compact: boolean }) {
  const { state, dispatch } = useCreateModel()
  const inputRef = useRef<HTMLInputElement>(null)
  const isFull = state.images.length >= MAX_IMAGES

  const processFiles = useCallback(
    async (fileList: FileList) => {
      const files = Array.from(fileList).filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type))
      const remaining = MAX_IMAGES - state.images.length
      const toProcess = files.slice(0, remaining)
      const newImages: TrainingImage[] = []

      for (const file of toProcess) {
        try {
          const { width, height } = await readImageDimensions(file)
          const objectUrl = URL.createObjectURL(file)
          const issues = detectIssues(width, height, file.size, [...state.images, ...newImages], file.name)
          newImages.push({
            id: crypto.randomUUID(), file, objectUrl, name: file.name,
            width, height, sizeBytes: file.size, format: file.type.split('/')[1] ?? 'unknown',
            caption: '', captionStatus: 'none', issues,
          })
        } catch { /* skip */ }
      }
      if (newImages.length > 0) dispatch({ type: 'ADD_IMAGES', payload: newImages })
    },
    [state.images, dispatch],
  )

  if (isFull) return null

  return (
    <div
      className={cn(
        'flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-border transition-colors hover:border-primary/50 hover:bg-primary/5',
        compact ? 'aspect-square' : 'aspect-square',
      )}
      onClick={() => inputRef.current?.click()}
    >
      <Plus className={cn('text-muted-foreground', compact ? 'h-4 w-4' : 'h-5 w-5')} />
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files) { processFiles(e.target.files); e.target.value = '' } }}
      />
    </div>
  )
}

// ─── Image Grid ─────────────────────────────────────────────────

export function ImageGrid() {
  const { state } = useCreateModel()
  const [compact, setCompact] = useState(false)

  if (state.images.length === 0) return null

  const cols = compact ? 5 : 3
  const expandedIndex = state.expandedImageId
    ? state.images.findIndex((i) => i.id === state.expandedImageId)
    : -1
  const editorAfterRow = expandedIndex >= 0 ? Math.floor(expandedIndex / cols) : -1

  // Build flat list of items: images + add card
  const items = [...state.images]
  const showAddCard = state.images.length < MAX_IMAGES

  // Group into rows
  const rows: (TrainingImage | 'add')[][] = []
  let rowItems: (TrainingImage | 'add')[] = []
  for (const img of items) {
    rowItems.push(img)
    if (rowItems.length === cols) {
      rows.push(rowItems)
      rowItems = []
    }
  }
  // Add the "+" card to the last partial row or start a new row
  if (showAddCard) rowItems.push('add')
  if (rowItems.length > 0) rows.push(rowItems)

  return (
    <div className="flex flex-col gap-2">
      {/* Grid density toggle */}
      {state.images.length > 6 && (
        <div className="flex items-center justify-end gap-1">
          <Button variant={compact ? 'ghost' : 'secondary'} size="icon-xs" onClick={() => setCompact(false)}>
            <Grid2X2 className="h-3.5 w-3.5" />
          </Button>
          <Button variant={compact ? 'secondary' : 'ghost'} size="icon-xs" onClick={() => setCompact(true)}>
            <Grid3X3 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Grid */}
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="flex flex-col gap-2">
          <div className={cn('grid gap-2', compact ? 'grid-cols-5' : 'grid-cols-3')}>
            {row.map((item) =>
              item === 'add' ? (
                <AddCard key="add" compact={compact} />
              ) : (
                <ImageCard key={item.id} image={item} compact={compact} />
              ),
            )}
          </div>
          {rowIndex === editorAfterRow && <CaptionEditor />}
        </div>
      ))}
    </div>
  )
}
